import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      // Backend already returns 200 to avoid account enumeration — only
      // unexpected errors land here.
      toast.error(err?.response?.data?.detail || "Couldn't send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
      </header>
      <main className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full max-w-md obs-card" data-testid="forgot-password-card">
          {sent ? (
            <>
              <div className="overline text-blaze mb-2">/ Check your inbox</div>
              <h1 className="font-heading text-4xl uppercase mb-3">Email sent.</h1>
              <p className="text-zinc-400 text-sm mb-6">
                If an account exists for <span className="text-zinc-100">{email}</span>, we've sent a
                password reset link. It expires in 30 minutes.
              </p>
              <p className="text-[11px] text-zinc-500 uppercase tracking-widest mb-6">
                Don't see it? Check spam, or try again in a minute.
              </p>
              <Link
                to="/auth/login"
                data-testid="forgot-password-back-to-login"
                className="text-blaze hover:text-blaze-hover text-sm inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </>
          ) : (
            <form onSubmit={submit}>
              <div className="overline text-blaze mb-2">/ Reset password</div>
              <h1 className="font-heading text-4xl uppercase mb-3">Forgot it?</h1>
              <p className="text-zinc-400 text-sm mb-6">
                Enter the email on your account. We'll send a one-time link to set a new password.
              </p>
              <label className="block mb-6">
                <div className="overline mb-1">Email</div>
                <input
                  data-testid="forgot-password-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
                />
              </label>
              <button
                data-testid="forgot-password-submit"
                disabled={loading}
                className="blaze-btn w-full py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <Mail className="h-4 w-4" /> {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="mt-6 text-sm text-zinc-400 text-center">
                Remembered it?{" "}
                <Link to="/auth/login" data-testid="forgot-password-to-login" className="text-blaze hover:text-blaze-hover">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
